import { CdkDragDrop, DragDropModule, moveItemInArray } from '@angular/cdk/drag-drop';
import {
  ChangeDetectionStrategy,
  Component,
  computed,
  effect,
  inject,
  input,
  output,
  signal,
  untracked,
} from '@angular/core';
import { MatButtonModule } from '@angular/material/button';
import { MatDialog } from '@angular/material/dialog';
import { MatIconModule } from '@angular/material/icon';
import { MatMenuModule } from '@angular/material/menu';
import { FlexLayoutModule } from '@ngbracket/ngx-layout';
import { TranslateModule } from '@ngx-translate/core';
import { NgxSkeletonLoaderModule } from 'ngx-skeleton-loader';
import { filter, forkJoin } from 'rxjs';

import { DisplayType, FieldTypeEnum } from '@models/module/fields/enums';
import { ConfirmationDialog } from '@shared/dialogs';
import { NotificationService } from '@shared/services';

import {
  ADDABLE_FIELD_TYPES,
  DeletedTableColumn,
  DisplayTypeDefinition,
  FIELD_TYPES,
  FieldBadges,
  fieldBadges,
  FieldDefinitionsService,
  fieldIcon,
  FieldTypeDefinition,
  TableColumnModel,
} from '../../common';
import { aggregateLabel } from '../field-formula/formula';

export interface TableColumnRequest {
  typeId: FieldTypeEnum;
  displayTypeId: DisplayType;
}

interface EditableColumn extends TableColumnModel {
  unsaved?: boolean;
}

const MAX_COLUMN_WIDTH = 10;

@Component({
  selector: 'cipo-table-columns',
  imports: [
    DragDropModule,
    FlexLayoutModule,
    MatButtonModule,
    MatIconModule,
    MatMenuModule,
    NgxSkeletonLoaderModule,
    TranslateModule,
  ],
  templateUrl: './table-columns.component.html',
  styleUrl: './table-columns.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class TableColumnsComponent {
  private readonly service = inject(FieldDefinitionsService);
  private readonly dialog = inject(MatDialog);
  private readonly notify = inject(NotificationService);

  readonly moduleId = input.required<number>();
  readonly tableId = input.required<number>();
  readonly tableTypeId = input.required<FieldTypeEnum>();
  readonly tableInUse = input(false);
  readonly disabled = input(false);

  readonly createColumn = output<TableColumnRequest>();
  readonly editColumn = output<number>();
  readonly editingChange = output<boolean>();

  readonly loading = signal(false);
  readonly saving = signal(false);
  readonly editing = signal(false);
  readonly columns = signal<EditableColumn[]>([]);
  readonly available = signal<(DeletedTableColumn & { unsaved?: boolean })[]>([]);

  private savedColumns: EditableColumn[] = [];

  readonly isTable = computed(() => this.tableTypeId() === FieldTypeEnum.Table);
  readonly isBusy = computed(() => this.loading() || this.saving() || this.disabled());
  readonly hasNoColumns = computed(() => !this.loading() && !this.columns().length && !this.available().length);
  readonly totalWidth = computed(() => this.columns().reduce((total, column) => total + (column.width || 1), 0));
  readonly canResize = computed(() => this.columns().length > 1);
  readonly maxWidth = MAX_COLUMN_WIDTH;
  readonly hasAggregates = computed(() => this.columns().some(column => !!column.aggregateId));
  readonly addableTypes = computed<FieldTypeDefinition[]>(() =>
    ADDABLE_FIELD_TYPES.filter(typeId => typeId !== FieldTypeEnum.Table).map(typeId => FIELD_TYPES[typeId]),
  );

  constructor() {
    effect(() => {
      const tableId = this.tableId();
      if (tableId) {
        untracked(() => this.reload());
      }
    });
  }

  reload(): void {
    this.loading.set(true);
    forkJoin({
      columns: this.service.getTableColumns(this.moduleId(), this.tableId()),
      available: this.service.getDeletedTableColumns(this.moduleId(), this.tableId()),
    }).subscribe({
      next: ({ columns, available }) => {
        const normalized = columns.map((column, index) => ({
          ...column,
          width: column.width || 1,
          position: column.position === 0 ? index : column.position,
        }));
        this.savedColumns = normalized;
        this.columns.set(normalized);
        this.available.set(available);
        this.loading.set(false);
      },
      error: () => this.loading.set(false),
    });
  }

  widthOf(column: EditableColumn): number {
    return (100 / this.totalWidth()) * (column.width || 1);
  }

  percentOf(column: EditableColumn): number {
    return Math.round(this.widthOf(column));
  }

  iconOf(column: TableColumnModel | DeletedTableColumn): string {
    return fieldIcon(column.typeId, column.displayTypeId);
  }

  badgesOf(column: TableColumnModel | DeletedTableColumn): FieldBadges {
    return fieldBadges(column);
  }

  columnAggregate(column: EditableColumn): string {
    return aggregateLabel(column.aggregateId);
  }

  isNumber(column: EditableColumn): boolean {
    return column.typeId === FieldTypeEnum.Number;
  }

  add(fieldType: FieldTypeDefinition, displayType: DisplayTypeDefinition): void {
    this.createColumn.emit({ typeId: fieldType.typeId, displayTypeId: displayType.displayTypeId });
  }

  edit(column: EditableColumn): void {
    this.editColumn.emit(column.id);
  }

  // MARK: edit mode

  startEditing(): void {
    this.setEditing(true);
  }

  cancelEditing(): void {
    this.columns.set(this.savedColumns);
    this.setEditing(false);
    this.reload();
  }

  drop(event: CdkDragDrop<EditableColumn[]>): void {
    this.move(event.previousIndex, event.currentIndex - event.previousIndex);
  }

  move(index: number, delta: number): void {
    const columns = [...this.columns()];
    moveItemInArray(columns, index, index + delta);
    this.columns.set(columns);
  }

  resize(column: EditableColumn, delta: number): void {
    const width = (column.width || 1) + delta;
    if (width < 1 || (delta > 0 && width > MAX_COLUMN_WIDTH)) {
      return;
    }
    this.columns.update(columns => columns.map(entry => (entry === column ? { ...entry, width } : entry)));
  }

  removeColumn(column: EditableColumn): void {
    this.columns.update(columns => columns.filter(entry => entry !== column));
    this.available.update(available => [...available, { ...column, unsaved: !!column.position }]);
  }

  addColumn(column: DeletedTableColumn): void {
    this.available.update(available => available.filter(entry => entry !== column));
    this.columns.update(columns => [
      ...columns,
      { ...(column as TableColumnModel), width: column.width || 1, unsaved: true },
    ]);
  }

  isChanged(column: EditableColumn, index: number): boolean {
    const saved = this.savedColumns[index];
    return !!column.unsaved || !saved || saved.id !== column.id || saved.width !== column.width;
  }

  save(): void {
    this.saving.set(true);
    const payload = {
      id: this.tableId(),
      fields: this.columns().map((column, index) => ({ id: column.id, width: column.width || 1, position: index + 1 })),
    };
    this.service.syncTableColumns(this.moduleId(), payload).subscribe({
      next: () => {
        this.notify.success('modules.fieldConfig.table.savedSuccess');
        this.saving.set(false);
        this.setEditing(false);
        this.reload();
      },
      error: () => this.saving.set(false),
    });
  }

  private setEditing(editing: boolean): void {
    this.editing.set(editing);
    this.editingChange.emit(editing);
  }

  deleteColumn(column: DeletedTableColumn): void {
    this.dialog
      .open(ConfirmationDialog, {
        data: {
          title: 'modules.fieldConfig.table.deleteColumnTitle',
          description: 'modules.fieldConfig.table.deleteColumnMessage',
          params: { name: column.name },
        },
      })
      .afterClosed()
      .pipe(filter(confirmed => !!confirmed))
      .subscribe(() => {
        this.saving.set(true);
        this.service.deleteTableColumn(this.moduleId(), column.id).subscribe({
          next: () => {
            this.available.update(available => available.filter(entry => entry !== column));
            this.saving.set(false);
          },
          error: () => this.saving.set(false),
        });
      });
  }
}
